import { useContext, useMemo } from 'react';
import { DateTime } from 'luxon';

import AccountContext from 'contexts/AccountContext';
import PeriodsContext from 'contexts/PeriodsContext';
import relativeDateRangeToDateRange from 'relativeDateRangeToDateRange';
import { AccountTodayOverrideContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import { FiscalPeriodsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';

/**
 * Pure resolver for a rolling date scope. Kept separate from the hook so it can
 * be unit-tested without a full context tree.
 *
 * The end is anchored to the selected bonus period: the period's end date, or
 * today ("to-date") when the period is in progress — the same rule the Periods
 * "Current Interval" scope uses. The start is the relative lookback measured
 * *back from that end reference* with calendar math, so it never depends on
 * period documents existing (unlike the Periods "Last N" path, which slices the
 * loaded periods array and fires no request once it runs out).
 */
export const getRollingDateScope = ({
  rolling,
  selectedPeriod,
  weekStartsOn,
  todayOverride,
  allFiscals,
}: {
  rolling: PerformanceBoardTypes.SlideOut.RollingDateFilter | undefined;
  selectedPeriod: Period | undefined;
  weekStartsOn: WeekStartsOn;
  todayOverride?: string;
  allFiscals?: FixedPeriods.AllFiscals;
}): DateRangeInput | undefined => {
  if (!rolling || !selectedPeriod) {
    return undefined;
  }

  const today = todayOverride
    ? DateTime.fromISO(todayOverride).toISODate()
    : DateTime.local().toISODate();

  const isInProgress =
    selectedPeriod.startDate <= today && today <= selectedPeriod.endDate;
  const endReference = isInProgress ? today : selectedPeriod.endDate;

  const { startDate } = relativeDateRangeToDateRange({
    // RollingLookback is a subset of RelativeDateRange; supply currentToDate here.
    relativeDateRange: { ...rolling.lookback, currentToDate: false },
    startOfWeek: weekStartsOn,
    comparedTo: endReference,
    todayOverride,
    fiscals: allFiscals,
  });

  return {
    startDate,
    endDate: endReference,
    dateField: rolling.dateField,
  };
};

const useSlideoutRollingDateScope = (
  rolling: PerformanceBoardTypes.SlideOut.RollingDateFilter | undefined,
): DateRangeInput | undefined => {
  const { selectedPeriod } = useContext(PeriodsContext);
  const { weekStartsOn } = useContext(AccountContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { allFiscals } = useContext(FiscalPeriodsContext);

  return useMemo(
    () =>
      getRollingDateScope({
        rolling,
        selectedPeriod,
        weekStartsOn,
        todayOverride,
        allFiscals,
      }),
    [rolling, selectedPeriod, weekStartsOn, todayOverride, allFiscals],
  );
};

export default useSlideoutRollingDateScope;
